import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Mail, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { PageLayout } from '../components/Layout';
import { SEO } from '../components/SEO';
import { apiClient } from '../lib/api';

export const UnsubscribePage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // 'loading', 'success', 'error'
  const [message, setMessage] = useState('');

  const userId = searchParams.get('user_id');
  const token = searchParams.get('token');

  useEffect(() => {
    const processUnsubscribe = async () => {
      if (!userId || !token) {
        setStatus('error');
        setMessage('Invalid unsubscribe link. Please check the link in your email.');
        return;
      }

      try {
        await apiClient.unsubscribeFromEmails(userId, token);
        setStatus('success');
        setMessage('You have been successfully unsubscribed from marketing emails.');
      } catch (error) {
        setStatus('error');
        setMessage(error.response?.data?.detail || 'Failed to unsubscribe. Please try again or contact support.');
      }
    };

    processUnsubscribe();
  }, [userId, token]);

  return (
    <PageLayout>
      <SEO 
        title="Unsubscribe" 
        description="Manage your email preferences"
        canonical="/unsubscribe"
      />
      
      <section className="py-16 bg-[#F8FAFC] min-h-[60vh] flex items-center">
        <div className="max-w-md mx-auto px-4 w-full">
          <Card className="border-slate-200">
            <CardContent className="pt-8 pb-6 text-center">
              {status === 'loading' && (
                <>
                  <div className="w-16 h-16 mx-auto mb-6 bg-[#0B1D51]/10 rounded-full flex items-center justify-center">
                    <Loader2 className="w-8 h-8 text-[#0B1D51] animate-spin" />
                  </div>
                  <h1 className="font-heading text-2xl font-bold text-[#0B1D51] mb-2">
                    Processing...
                  </h1>
                  <p className="text-[#64748B]">
                    Please wait while we update your preferences.
                  </p>
                </>
              )}

              {status === 'success' && (
                <>
                  <div className="w-16 h-16 mx-auto mb-6 bg-[#2CB67D]/10 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-[#2CB67D]" />
                  </div>
                  <h1 className="font-heading text-2xl font-bold text-[#0B1D51] mb-2">
                    Unsubscribed
                  </h1>
                  <p className="text-[#64748B] mb-6">
                    {message}
                  </p>
                  <p className="text-sm text-[#64748B] mb-6">
                    You will no longer receive marketing emails from TDF Psychometrics. 
                    You will still receive important account-related emails.
                  </p>
                  <Link to="/">
                    <Button variant="outline">
                      Return to Homepage
                    </Button>
                  </Link>
                </>
              )}

              {status === 'error' && (
                <>
                  <div className="w-16 h-16 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
                    <XCircle className="w-8 h-8 text-red-500" />
                  </div>
                  <h1 className="font-heading text-2xl font-bold text-[#0B1D51] mb-2">
                    Oops!
                  </h1>
                  <p className="text-[#64748B] mb-6">
                    {message}
                  </p>
                  <div className="space-y-3">
                    <Link to="/contact">
                      <Button className="w-full bg-[#FF9F1C] hover:bg-[#FF9F1C]/90">
                        Contact Support
                      </Button>
                    </Link>
                    <Link to="/">
                      <Button variant="outline" className="w-full">
                        Return to Homepage
                      </Button>
                    </Link>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </section>
    </PageLayout>
  );
};

export default UnsubscribePage;
